import { createApi } from "@reduxjs/toolkit/query/react";
import { format } from "date-fns";

import { ISO8601_DATE_FORMAT } from "@constants";

import { baseQuery } from "../../../shared/api/base-query";
import { createQueryFn } from "../../../shared/api/create-query-fn";
import { PagedResponseDto } from "../../../shared/dtos/paged-response.dto";
import { OrderGroup } from "../../../shared/interface";
import { buildFilterCondition } from "../../../shared/utils/build-filter-condition";
import { buildFilterExpression } from "../../../shared/utils/build-filter-expression";
import { TObservationsRequest } from "../containers/observations-list/observations-list-utils";
import { IObservationPhotoUploadDto } from "../shared/dtos/observation-photo-upload.dto";
import { IObservationUpsertDto } from "../shared/dtos/observation-upsert.dto";
import { IObservationDto } from "../shared/dtos/observation.dto";
import { Observation } from "../shared/models/observation";

const observationsUrl = "ObservationsV3";
const observationPhotosUrl = "ObservationPhotosV2";

type TUploadObservationPhotosArgs = {
  observationId: string;
  appUserId: string;
  files: File[];
};

export const observationsApiSlice = createApi({
  reducerPath: "api-observations",
  baseQuery,
  tagTypes: ["ObservationsList"],
  endpoints: (builder) => ({
    observationsList: builder.query<
      { items: Observation[]; totalCount: number },
      TObservationsRequest
    >({
      query: (request: TObservationsRequest) => {
        const { paginationState, onlyOriginal } = request;
        const { limit, offset, order } = paginationState;
        const pageNumber = Math.floor(offset / limit) + 1;
        const sort = order.length > 0 ? buildSortString(order) : undefined;
        const filter = buildObservationsFilter(request);
        return {
          url: observationsUrl,
          params: {
            pageNumber,
            pageSize: limit,
            sort,
            filter,
            ...(onlyOriginal !== undefined && { onlyOriginal }),
          },
          method: "GET",
        };
      },
      transformResponse: (response: PagedResponseDto<IObservationDto>) => ({
        items: response.data.map((dto) => Observation.fromDto(dto)),
        totalCount: response.meta.totalCount,
      }),
      providesTags: ["ObservationsList"],
    }),

    removeObservation: builder.mutation<void, string>({
      query: (id) => ({
        url: `${observationsUrl}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ObservationsList"],
    }),

    getObservation: builder.query<IObservationDto, string>({
      queryFn: createQueryFn(async (id, executeBaseQuery) => {
        // ObservationsV3 не имеет GET {id}: берём через list с фильтром по id
        // (pageNumber обязателен при фильтре, см. GetPagesQuery.IsBatching).
        // onlyOriginal=true переключает бэкенд на GetBaseQueryFromOriginalTable.
        const response = await executeBaseQuery({
          url: observationsUrl,
          method: "GET",
          params: {
            pageNumber: 1,
            pageSize: 1,
            filter: buildFilterCondition("id", "==", id),
            onlyOriginal: true,
          },
        });
        if (response.error !== undefined) {
          return { error: response.error };
        }
        if (!isObservationPagedResponse(response.data)) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Invalid ObservationsV3 response",
            },
          };
        }
        const dto = response.data.data[0];
        if (!dto) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: `Observation with id=${id} not found`,
            },
          };
        }
        return { data: filterIsDeletedPhotos(dto) };
      }),
      providesTags: ["ObservationsList"],
    }),

    addObservation: builder.mutation<IObservationDto, IObservationUpsertDto>({
      query: (dto) => ({
        url: observationsUrl,
        method: "POST",
        body: dto,
      }),
      invalidatesTags: ["ObservationsList"],
    }),

    updateObservation: builder.mutation<IObservationDto, IObservationUpsertDto>({
      query: ({ id, ...dto }) => {
        if (!id) {
          throw new Error("Can't update entity without id");
        }
        return {
          url: `${observationsUrl}/${id}`,
          method: "PUT",
          body: { ...dto, cropId: undefined },
        };
      },
      invalidatesTags: ["ObservationsList"],
    }),

    completeObservationReport: builder.mutation<IObservationDto | undefined, string>({
      query: (observationId) => ({
        url: `${observationsUrl}/${observationId}`,
        method: "PATCH",
        body: { isReportComplete: true },
        responseHandler: parseOptionalJson,
      }),
      transformResponse: (data: unknown) => {
        if (data === null) {
          return undefined;
        }
        if (!isObservationDto(data)) {
          throw new Error("Invalid ObservationsV3 report response");
        }
        return data;
      },
      invalidatesTags: ["ObservationsList"],
    }),

    deleteObservationPhoto: builder.mutation<void, string>({
      query: (id) => ({
        url: `${observationPhotosUrl}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ObservationsList"],
    }),

    uploadObservationPhotos: builder.mutation<
      IObservationPhotoUploadDto[],
      TUploadObservationPhotosArgs
    >({
      query: buildUploadObservationPhotosQuery,
      invalidatesTags: ["ObservationsList"],
    }),
  }),
});

function filterIsDeletedPhotos(dto: IObservationDto): IObservationDto {
  return {
    ...dto,
    photos: dto.photos.filter((photoDto) => !photoDto.isDeleted),
  };
}

async function parseOptionalJson(response: Response): Promise<unknown> {
  const body = await response.text();
  if (!body.trim()) {
    return null;
  }

  try {
    return JSON.parse(body);
  } catch {
    if (!response.ok) {
      return body;
    }
    throw new Error("Invalid JSON in ObservationsV3 response");
  }
}

function isObservationPagedResponse(
  response: unknown
): response is PagedResponseDto<IObservationDto> {
  if (response === null || typeof response !== "object" || !("data" in response)) {
    return false;
  }
  return Array.isArray(response.data) && response.data.every(isObservationDto);
}

function isObservationDto(value: unknown): value is IObservationDto {
  return (
    value !== null &&
    typeof value === "object" &&
    "id" in value &&
    typeof value.id === "string" &&
    "photos" in value &&
    Array.isArray(value.photos)
  );
}

export function buildUploadObservationPhotosQuery({
  observationId,
  appUserId,
  files,
}: TUploadObservationPhotosArgs) {
  const data = new FormData();
  files.forEach((file) => {
    data.append("photos", file);
  });
  return {
    url: `${observationsUrl}/${observationId}/uploadPhotos`,
    method: "POST",
    params: { appUserId },
    body: data,
  };
}

function buildObservationsFilter(request: TObservationsRequest): string {
  const { filtersState, seasonId, farmId } = request;
  const { from, to, cropType, farmLandId, phenoPhaseId } = filtersState;

  return buildFilterExpression(
    buildFilterCondition("seasonId", "==", seasonId),
    buildFilterCondition("farmId", "==", farmId),
    from &&
      buildFilterCondition("observationDate", ">=", format(from, ISO8601_DATE_FORMAT)),
    to && buildFilterCondition("observationDate", "<=", format(to, ISO8601_DATE_FORMAT)),
    cropType?.id && buildFilterCondition("cropTypeId", "==", cropType.id),
    farmLandId && buildFilterCondition("farmLandId", "==", farmLandId),
    phenoPhaseId && buildFilterCondition("phenoPhaseId", "==", phenoPhaseId)
  );
}

function buildSortString(order: OrderGroup): string {
  return order.map(([field, direction]) => `${String(field)} ${direction}`).join(", ");
}

export const { useObservationsListQuery, useRemoveObservationMutation } =
  observationsApiSlice;
